/**
 * Notifications Module
 * UI for viewing and managing incoming share invitations and notifications
 * (formerly invitations.js)
 */

import { createModal, showModal, closeModal } from "./modals.js";
import { escapeHtml, escapeAttr } from "../../../engine/core/validation.js";
import { VaultStorage } from "../../../engine/crypto/vault-storage.js";
import { emit, on, Events } from "../../../engine/core/events.js";
import { keypairManager, roleInText } from "./share-modal.js";
import * as MapKeyTrust from '../../../engine/crypto/map-key-trust.js';
import { Keypair } from "../../../engine/crypto/keypair.js";
import { decryptInviteTitle } from "../../../engine/crypto/invite-title.js";
import { confirmKeyDecision, confirmEnrollment, enrollmentNeedsConfirmation } from "./identity-repair.js";
import { isRevocationProcessed } from "../../../engine/sync/share-revocation.js";
import { decompress } from "../../../engine/sync/compression.js";
import * as Y from "yjs";
import { IndexeddbPersistence } from "y-indexeddb";
import { t, formatRelative, formatDate as formatLocaleDate } from "../../../shared/i18n/index.js";
import { showToast } from "./toast.js";

// ============================================
// State
// ============================================

let invitations = [];
let notifications = [];
let unsubscribeNotifications = null; // Cleanup function for WebSocket listener
let _moduleUnsubscribers = []; // Cleanup for module-level event listeners
/** Track notification IDs dismissed locally to filter out self-echo from WS broadcast */
const _locallyDismissed = new Set();
/** Track share IDs already handled via WS `removed` (active docs). If the share_revoked
 *  notification arrives later, it's auto-dismissed instead of queued. */
const _alreadyRevokedShares = new Set();
/** The server's title of every share_invitation since S-M1 (INVITATION_TITLE in
 *  invitations.rs). Not shown as is: the app shows its own, translated. */
const SERVER_INVITATION_TITLE = "Shared map";

// ============================================
// Initialization
// ============================================

/**
 * Initialize the notifications module
 * Fetches pending notifications on login and listens for real-time updates via WebSocket
 */
export async function initInvitations() {
  // Listen for real-time notifications via User WebSocket
  // Store unsubscribe function for cleanup on logout
  unsubscribeNotifications = on(Events.USER_NOTIFICATION_RECEIVED, handleRealTimeNotification);

  // Refetch from REST every time the user-WS reconnects. The push channel
  // is fire-and-forget on the server: any notification created while we
  // were disconnected (e.g., backend restart, transient drop, full
  // channel) is never replayed, so the client's in-memory list — and the
  // badge — would silently drift out of sync forever. Refetching on
  // reconnect resyncs both. Track a previous-state to only fire on the
  // disconnected→connected edge, not on every CONNECTING/SYNCING tick.
  let _wasConnected = false;
  _moduleUnsubscribers.push(
    on(Events.USER_CRDT_STATE_CHANGED, ({ state }) => {
      const isConnected = state === "connected" || state === "synced";
      if (isConnected && !_wasConnected) {
        _wasConnected = true;
        fetchPendingNotifications().catch((err) => {
          console.warn("[Notifications] reconnect refetch failed:", err);
        });
      } else if (!isConnected) {
        _wasConnected = false;
      }
    }),
  );

  // Fetch pending notifications from REST API (for offline users)
  try {
    await fetchPendingNotifications();
  } catch (error) {
    console.error("[Notifications] Failed to fetch pending notifications:", error);
  }

  console.log("[Notifications] Module initialized");
}

/**
 * Fetch pending notifications from REST API
 * This catches notifications that arrived while the user was offline
 */
async function fetchPendingNotifications() {
  const response = await fetch("/api/notifications", {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch notifications: ${response.status}`);
  }

  const pendingNotifications = await response.json();
  console.log("[Notifications] Fetched pending notifications:", pendingNotifications.length);

  // Process each notification through the same handler
  // Convert REST API format to WebSocket format for consistency
  for (const notif of pendingNotifications) {
    const wsFormatNotification = {
      id: notif.id,
      type: notif.notification_type, // REST uses notification_type, WS uses type
      title: notif.title,
      message: notif.message,
      metadata: notif.metadata,
      timestamp: new Date(notif.created_at).getTime(),
    };
    handleRealTimeNotification(wsFormatNotification);
  }
}

/**
 * Cleanup the notifications module
 */
export function destroyInvitations() {
  if (unsubscribeNotifications) {
    unsubscribeNotifications();
    unsubscribeNotifications = null;
  }
  // Clean up module-level listeners to prevent duplicates on re-login
  for (const unsub of _moduleUnsubscribers) {
    if (typeof unsub === 'function') unsub();
  }
  _moduleUnsubscribers = [];
  invitations = [];
  notifications = [];
  _locallyDismissed.clear();
  _alreadyRevokedShares.clear();
  updateBadge(); // Clear the badge on logout
}

// ============================================
// API Functions (REST endpoints - used for actions, not polling)
// ============================================

// Note: We no longer poll REST APIs. Notifications arrive via WebSocket.
// These functions are kept for backwards compatibility but won't be called regularly.

/**
 * Mark a notification as read
 * @param {string} notificationId - The notification UUID
 */
export async function markNotificationRead(notificationId) {
  console.log("[Notifications] Marking notification as read:", notificationId);

  // Remove from local list and track BEFORE the await so the WS self-echo
  // (NotificationDismissed broadcast) is filtered even if it arrives during fetch
  notifications = notifications.filter((n) => n.id !== notificationId);
  _locallyDismissed.add(notificationId);
  updateBadge();

  const response = await fetch(`/api/notifications/${notificationId}/read`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
    credentials: "include",
  });

  // 404 means notification was already read/deleted - that's fine
  if (!response.ok && response.status !== 404) {
    throw new Error(t("share.inbox.markReadFailed"));
  }
}

/**
 * Dismiss any share_revoked notifications for a specific share
 * Called when WebSocket revocation is handled (to avoid duplicate notification)
 * @param {string} shareId - The share UUID
 */
async function dismissShareRevokedNotification(shareId) {
  // Find notifications for this share in local list
  // (notifications arrive via WebSocket, so we should have them)
  const matching = notifications.filter(
    (n) =>
      n.notification_type === "share_revoked" &&
      n.metadata?.share_id === shareId,
  );

  // Mark each as read (removes from local list)
  for (const notif of matching) {
    try {
      await markNotificationRead(notif.id);
    } catch (e) {
      // REST API may not work, but we can still remove from local list
      console.warn("[Notifications] Failed to mark notification as read:", e);
      notifications = notifications.filter((n) => n.id !== notif.id);
      updateBadge();
    }
  }
}

/**
 * The server's copy of one of our pending invitations (with the wrapped DEK
 * and the map owner), or null when it no longer exists.
 */
async function fetchPendingInvitation(invitationId) {
  const response = await fetch("/api/invitations", { credentials: "include" });
  if (!response.ok) throw new Error(t("share.inbox.loadInvitationsFailed"));
  const list = await response.json().catch(() => []);
  return (Array.isArray(list) && list.find((i) => i.id === invitationId)) || null;
}

/**
 * Accept an invitation
 * @param {string} invitationId - The invitation UUID
 * @returns {Promise<Object>} The accepted share permission
 */
export async function acceptInvitation(invitationId) {
  // The map title becomes the vault entry's title: decrypt it first (S-M1).
  await resolveInvitationTitles();
  // Find the invitation first to get notification ID
  const invitation = invitations.find((i) => i.id === invitationId);

  // Verify the map's key authority BEFORE the server commits the membership.
  // Accepting first and verifying afterwards left a half-accepted state when
  // verification failed: the server listed us as a member, our vault had no
  // entry, the invitation was consumed and re-inviting said "already has
  // access". Now a failed check leaves the invitation pending, with the reason.
  const pending = await fetchPendingInvitation(invitationId);
  let verifiedAuthority;
  if (pending) {
    const details = await MapKeyTrust.inspectInvitation(pending.doc_id, pending.wrapped_dek, pending.owner_username);
    // S-L1 / S-L5: an admin's invitation, a key that disagrees with the
    // vault's pins, or a map whose key can't be verified at all: show who
    // and which keys, and let the user decide.
    if (enrollmentNeedsConfirmation(details) && !(await confirmEnrollment(details, {
      title: invitation?.share_title, ownerUsername: pending.owner_username, inviterUsername: pending.inviter_username,
    }))) {
      throw joinCancelled();
    }
    verifiedAuthority = details.state;
  }

  const response = await fetch(`/api/invitations/${invitationId}/accept`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
    credentials: "include",
  });

  if (!response.ok) {
    // 404 means the invitation row is gone server-side — typically because
    // it was already consumed (race between two auto-accept attempts, or
    // server-side auto-grant from a pending access-request). Don't just
    // give up: if the invitee actually has a doc_keys row now, refetch
    // their wrappedDEK and seed the local vault from it. Without this
    // step the recipient ends up with a stale or missing vault entry,
    // even though server-side access is fine — leading to baseline-
    // decrypt failures that self-heal can't recover from (it would
    // compare local stale wrap with server stale wrap and find them
    // equal, since reconcile gives up if local already matches server).
    if (response.status === 404) {
      console.log("[Invitations] Invitation no longer exists, attempting to recover via /my-dek:", invitationId);
      const docIdGuess = invitation?.share_id;
      let recovered = null;
      if (docIdGuess) {
        try {
          const myDekResp = await fetch(`/api/shares/${encodeURIComponent(docIdGuess)}/my-dek`, {
            credentials: "include",
          });
          if (myDekResp.ok) {
            const { wrapped_dek, is_revoked } = await myDekResp.json().catch(() => ({}));
            if (wrapped_dek && !is_revoked) {
              recovered = {
                doc_id: docIdGuess,
                role: invitation?.role || "viewer",
                wrapped_dek,
              };
            }
          }
        } catch (err) {
          console.warn("[Invitations] /my-dek recovery probe failed:", err);
        }
      }

      if (invitation?.notificationId) {
        await markNotificationRead(invitation.notificationId).catch(() => {});
      }
      invitations = invitations.filter((i) => i.id !== invitationId);
      updateBadge();

      if (recovered) {
        const details = await MapKeyTrust.inspectEnrollment(recovered.doc_id, recovered.wrapped_dek);
        if (enrollmentNeedsConfirmation(details) && !(await confirmEnrollment(details, { title: invitation?.share_title }))) {
          throw joinCancelled();
        }
        const keyAuthority = details.state;
        MapKeyTrust.rememberIdentities(recovered.doc_id, keyAuthority);
        // Treat as if accept had succeeded: write the server-confirmed
        // wrap into the vault, pre-cache, and let the caller's success
        // path (toast, AccessRequests.markResolved, ensureLinkedDocSync
        // via VAULT_INDEX_UPDATED) run as normal.
        console.log("[Invitations] Recovered from 404 via /my-dek for doc:", docIdGuess);
        await VaultStorage.setDocEntry({
          docId: recovered.doc_id,
          title: invitation.share_title,
          folderId: null,
          role: recovered.role,
          wrappedDEK: recovered.wrapped_dek,
          ...(keyAuthority && { keyAuthority }),
          ownerUsername: invitation.inviter_username,
        });
        preCacheSharedData(recovered.doc_id, invitation.share_title);
        emit(Events.INVITATION_ACCEPTED, {
          invitationId,
          docId: recovered.doc_id,
        });
        return recovered;
      }
      throw new Error(t("share.inbox.invitationGone"));
    }
    const error = await response.json();
    throw new Error(error.error || t("share.inbox.acceptFailed"));
  }

  const permission = await response.json();

  // Store the doc entry in unified vault storage. Always — the server has
  // committed the membership, so a missing local list entry (e.g. an accept
  // triggered before the notification list loaded) must not leave the map
  // out of the vault.
  const docId = permission.doc_id || permission.share_id;
  const title = invitation?.share_title || t("share.invite.defaultTitle");
  const keyAuthority = pending && permission.wrapped_dek === pending.wrapped_dek
    ? verifiedAuthority
    : await MapKeyTrust.enroll(docId, permission.wrapped_dek);
  MapKeyTrust.rememberIdentities(docId, keyAuthority);
  await VaultStorage.setDocEntry({
    docId,
    title,
    folderId: null,
    role: permission.role,
    wrappedDEK: permission.wrapped_dek,
    ...(keyAuthority && { keyAuthority }),
    ownerUsername: pending?.owner_username || invitation?.inviter_username,
  });

  // Pre-cache the shared data in the background
  // This ensures data is available for recovery if access is later revoked
  preCacheSharedData(docId, title);

  // Mark the associated notification as read on the server
  if (invitation?.notificationId) {
    await markNotificationRead(invitation.notificationId).catch(() => {});
  }

  // Remove from local list
  invitations = invitations.filter((i) => i.id !== invitationId);
  updateBadge();

  // S-M8: re-invited after a revocation. The revocation notice (Recover)
  // stayed until now, so declining would still have let us recover; joining
  // makes it obsolete (the server deletes it too).
  for (const stale of notifications.filter((n) => n.notification_type === "share_revoked" && n.metadata?.share_id === docId)) {
    markNotificationRead(stale.id).catch(() => {
      notifications = notifications.filter((n) => n.id !== stale.id);
      updateBadge();
    });
  }

  emit(Events.INVITATION_ACCEPTED, {
    invitationId,
    docId: permission.doc_id || permission.share_id,
  });

  return permission;
}

/** The user chose not to join (a key or join confirmation was cancelled). */
function joinCancelled() {
  const error = new Error(t("share.inbox.notAccepted"));
  error.code = "cancelled";
  return error;
}

let _titleResolution = null;

/**
 * S-M1: invitation titles travel encrypted under the map key. Unwrap each
 * pending invitation's key with our secret key and decrypt its title.
 * Display only: until the map is accepted and verified, a title is as
 * trustworthy as the invitation itself.
 */
function resolveInvitationTitles() {
  if (!invitations.some((i) => i.encryptedTitle && !i.titleResolved)) return Promise.resolve();
  if (_titleResolution) return _titleResolution;
  _titleResolution = (async () => {
    const stored = await VaultStorage.getKeypair?.();
    if (!stored?.secretKey) return;
    const response = await fetch("/api/invitations", { credentials: "include" });
    if (!response.ok) return;
    const list = await response.json().catch(() => []);
    const byId = new Map((Array.isArray(list) ? list : []).map((i) => [i.id, i]));
    let changed = false;
    for (const inv of invitations.filter((i) => i.encryptedTitle && !i.titleResolved)) {
      const server = byId.get(inv.id);
      if (!server?.wrapped_dek) continue;
      const secret = Keypair.fromBase64(stored.secretKey);
      let dek = null;
      try {
        dek = await Keypair.unwrapDEKAnonymous(Keypair.fromBase64(server.wrapped_dek), secret);
        const title = await decryptInviteTitle(dek, server.doc_id, inv.encryptedTitle);
        if (title) { inv.share_title = title; changed = true; }
      } catch (err) {
        console.warn("[Invitations] Couldn't decrypt an invitation title:", err?.message || err);
      } finally {
        inv.titleResolved = true;
        secret.fill(0);
        dek?.fill(0);
      }
    }
    if (changed) updateBadge();
  })()
    .catch((err) => console.warn("[Invitations] Title resolution failed:", err))
    .finally(() => { _titleResolution = null; });
  return _titleResolution;
}

// One join question at a time.
let _joinPrompts = Promise.resolve();

/**
 * S-L5: an invitation that answers the user's own access request used to be
 * accepted silently. Ask first; "Later" leaves it in the inbox.
 */
function promptJoinGrantedRequest(invitationId, docId) {
  _joinPrompts = _joinPrompts.then(async () => {
    await resolveInvitationTitles();
    const inv = invitations.find((i) => i.id === invitationId);
    if (!inv) return;
    const title = inv.share_title || t("share.join.aMap");
    const join = await confirmKeyDecision({
      title: t("share.join.title"),
      paragraphs: [t("share.join.message", { name: inv.inviter_username || t("share.join.theOwner"), title, role: roleInText(inv.role) })],
      confirmText: t("share.join.confirm"),
      cancelText: t("common.later"),
    });
    if (!join) return;
    try {
      await acceptInvitation(invitationId);
    } catch (err) {
      if (err?.code !== "cancelled") console.warn("[Notifications] Joining failed — left in the inbox:", err);
      return;
    }
    if (docId) {
      const { markResolved } = await import("../../../engine/sync/access-requests.js");
      markResolved(docId);
    }
    const { showToast } = await import("./toast.js");
    showToast(t("share.join.toast", { title }), { type: "success" });
  }).catch((err) => console.warn("[Notifications] Join prompt failed:", err));
  return _joinPrompts;
}

// Docs whose adoption was already attempted this session (success or not).
const _adoptionAttempted = new Set();

/**
 * Heal memberships stranded by an accept that committed on the server but
 * failed before the vault write (clients before verify-first accept): the map
 * never appeared, the invitation was gone and re-inviting said "already has
 * access". Each live non-owner membership missing from the vault is offered
 * once per session — only if its key authority verifies, exactly as
 * accepting would. Unverifiable ones are left alone (and logged).
 *
 * S-L5: never joined silently. The user sees the maps (owner, key
 * fingerprint, and whether the map's key can be verified at all) and decides.
 *
 * @param {Array|null} shares - GET /api/shares rows, if already fetched
 * @returns {Promise<number>} number of maps added to the vault
 */
export async function adoptStrandedMemberships(shares = null) {
  if (!VaultStorage.getIndex?.()) return 0;
  if (!shares) {
    const response = await fetch("/api/shares", { credentials: "include" });
    if (!response.ok) return 0;
    shares = await response.json().catch(() => []);
  }
  const candidates = [];
  for (const share of Array.isArray(shares) ? shares : []) {
    const docId = share.doc_id || share.id;
    if (!docId || share.role === "owner" || !share.wrapped_dek) continue;
    if (VaultStorage.getDoc(docId) || _adoptionAttempted.has(docId)) continue;
    _adoptionAttempted.add(docId);
    try {
      const info = await fetch(`/api/shares/${docId}`, { credentials: "include" });
      const ownerUsername = info.ok ? (await info.json()).owner_username || null : null;
      const details = await MapKeyTrust.inspectEnrollment(docId, share.wrapped_dek, { ownerUsername });
      candidates.push({ share, docId, ownerUsername, details });
    } catch (err) {
      console.warn("[Invitations] Accepted map could not be verified; not added to the vault:", docId, err?.message || err);
    }
  }
  if (!candidates.length) return 0;

  const mismatches = candidates.flatMap((c) => c.details.mismatches || []);
  const join = await confirmKeyDecision({
    title: t("share.adopt.title", { count: candidates.length }),
    paragraphs: [t("share.adopt.explain")],
    rows: candidates.map((c) => ({
      label: c.ownerUsername ? t("share.inbox.from", { name: c.ownerUsername }) : t("share.invite.defaultTitle"),
      key: c.details.owner?.publicKey,
      note: c.details.state ? roleInText(c.share.role) : t("share.adopt.unverified", { role: roleInText(c.share.role) }),
    })),
    warnings: mismatches.map((m) => (m.username
      ? t("share.adopt.keyDiffers", { name: m.username })
      : t("share.adopt.keyDiffersUnknown"))),
    checkbox: mismatches.length ? t("share.adopt.checkbox") : null,
    confirmText: candidates.length === 1 ? t("share.adopt.add") : t("share.adopt.addAll"),
    cancelText: t("share.adopt.notNow"),
  });
  if (!join) return 0;

  let adopted = 0;
  for (const { share, docId, ownerUsername, details } of candidates) {
    try {
      for (const m of details.mismatches || []) MapKeyTrust.confirmIdentity(m.userId, m.publicKey, m.username);
      const keyAuthority = details.state;
      const title = ownerUsername ? t("share.adopt.entryTitle", { name: ownerUsername }) : t("share.invite.defaultTitle");
      await VaultStorage.setDocEntry({
        docId,
        title,
        folderId: null,
        role: share.role,
        wrappedDEK: share.wrapped_dek,
        ...(keyAuthority && { keyAuthority }),
        ownerUsername,
      });
      MapKeyTrust.rememberIdentities(docId, keyAuthority);
      preCacheSharedData(docId, title);
      adopted++;
      console.log("[Invitations] Restored an accepted map that was missing from the vault:", docId);
    } catch (err) {
      console.warn("[Invitations] Couldn't add an accepted map to the vault:", docId, err?.message || err);
    }
  }
  return adopted;
}

/**
 * Decline an invitation
 * @param {string} invitationId - The invitation UUID
 */
/**
 * Ignore every invitation from the person who sent this one: theirs waiting
 * here go (the server drops them and their notifications), later ones are
 * refused (handlers/shares/ignores.rs).
 * @returns {Promise<{username: string, dropped: string[]}>} the invitation ids gone
 */
export async function ignoreInviter(invitationId) {
  const invitation = invitations.find((i) => i.id === invitationId);
  const response = await fetch(`/api/invitations/${invitationId}/ignore`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
    credentials: "include",
  });
  if (!response.ok && response.status !== 404) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || t("share.inbox.ignoreFailed"));
  }
  const body = response.ok ? await response.json().catch(() => ({})) : {};
  const name = body.username || invitation?.inviter_username;
  // What the server dropped (all from this person), this one in any case.
  const dropped = new Set([invitationId, ...(Array.isArray(body.dropped) ? body.dropped : [])]);
  invitations = invitations.filter((i) => !dropped.has(i.id));
  updateBadge();
  for (const id of dropped) emit(Events.INVITATION_DECLINED, { invitationId: id });
  return { username: name, dropped: [...dropped] };
}

export async function declineInvitation(invitationId) {
  // Find the invitation first to get notification ID
  const invitation = invitations.find((i) => i.id === invitationId);

  const response = await fetch(`/api/invitations/${invitationId}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
    credentials: "include",
  });

  // 404 means invitation already gone - that's fine for decline
  if (!response.ok && response.status !== 404) {
    const error = await response.json();
    throw new Error(error.error || t("share.inbox.declineError"));
  }

  // Mark the associated notification as read on the server
  if (invitation?.notificationId) {
    await markNotificationRead(invitation.notificationId).catch(() => {});
  }

  // Remove from local list (whether server returned success or 404)
  invitations = invitations.filter((i) => i.id !== invitationId);
  updateBadge();

  emit(Events.INVITATION_DECLINED, { invitationId });
}

// ============================================
// UI Functions
// ============================================

/**
 * Update the invitation badge count
 */
function updateBadge() {
  // Mobile has no DOM badge — it re-renders its bell from the lists on this
  // event. Fired for invitation AND generic notification changes alike.
  emit(Events.INVITATIONS_UPDATED, {
    count: invitations.length + notifications.length,
  });
  const badge = document.getElementById("notification-badge");
  if (badge) {
    const totalCount = invitations.length + notifications.length;
    if (totalCount > 0) {
      badge.textContent = totalCount;
      badge.style.display = "block";
    } else {
      badge.style.display = "none";
    }
  }
}

/**
 * Get the current invitation count
 */
export function getInvitationCount() {
  return invitations.length;
}

/**
 * Get all pending invitations
 */
export function getInvitations() {
  return [...invitations];
}

/**
 * Get all pending non-invitation notifications (share_revoked,
 * access_request, share_accepted, ...). Used by the mobile notifications
 * sheet; the desktop modal renders the same list.
 */
export function getPendingNotifications() {
  return [...notifications];
}

/**
 * Run the share-revocation recovery for a notification. Shared by the
 * desktop modal's Recover button and the mobile notifications sheet: emits
 * REQUEST_SHARE_REVOCATION (handled platform-neutrally in crdt-bridge →
 * share-revocation.js) and resolves with the SHARE_REVOKED outcome. On
 * success (or explicit skip) the notification is marked read.
 *
 * @returns {Promise<{recovered?: boolean, recoveryFailed?: boolean, recoveredDocId?: string}>}
 */
export async function recoverRevokedShare(notificationId, shareId, shareTitle) {
  const result = await new Promise((resolve) => {
    const timeout = setTimeout(() => resolve({ recoveryFailed: true }), 15000);
    const unsub = on(Events.SHARE_REVOKED, (data) => {
      if (data.shareId === shareId) {
        clearTimeout(timeout);
        unsub();
        resolve(data);
      }
    });
    emit(Events.REQUEST_SHARE_REVOCATION, {
      shareId,
      title: shareTitle,
      options: { fromNotification: true },
    });
  });
  if (!result.recoveryFailed && notificationId) {
    await markNotificationRead(notificationId).catch(() => {
      notifications = notifications.filter((n) => n.id !== notificationId);
      updateBadge();
    });
  }
  return result;
}

/**
 * Show the invitations dropdown/modal
 */
export async function showInvitationsModal() {
  // Refetch from REST before rendering. WS push delivers notifications
  // in real-time when the user-WS is connected, but pushes can be lost
  // (server restart, transient disconnect, full channel, etc.) and the
  // in-memory list never recovers from a missed push on its own. A
  // refetch here guarantees the user sees truth whenever they actually
  // open the inbox.
  try {
    await fetchPendingNotifications();
    await resolveInvitationTitles();
  } catch (err) {
    console.warn("[Notifications] inbox-open refetch failed:", err);
  }

  return new Promise((resolve) => {
    const modalId = "invitations-modal";

    // Build notifications HTML
    let notificationsHtml = "";
    if (notifications.length > 0) {
      notificationsHtml = `
        <div class="notifications-section">
          <h4 class="section-title">${escapeHtml(t("share.inbox.notifications"))}</h4>
          ${notifications
            .map(
              (notif) => `
            <div class="notification-item ${['share_invitation', 'share_revoked', 'share_accepted', 'dek_rotated', 'share_role_changed', 'access_request'].includes(notif.notification_type) ? notif.notification_type : ''}" data-id="${escapeAttr(notif.id)}" data-type="${escapeAttr(notif.notification_type)}" data-share-id="${escapeAttr(notif.metadata?.share_id || "")}" data-share-title="${escapeAttr(notif.metadata?.share_title || "")}" data-doc-id="${escapeAttr(notif.metadata?.doc_id || "")}" data-requester-id="${escapeAttr(notif.metadata?.requester_id || "")}" data-requester-username="${escapeAttr(notif.metadata?.requester_username || "")}" data-doc-title="${escapeAttr(notif.metadata?.doc_title || "")}">
              <div class="notification-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </div>
              <div class="notification-info">
                <div class="notification-title">${escapeHtml(notif.title)}</div>
                <div class="notification-message">${escapeHtml(notif.message || "")}</div>
                <div class="notification-date">${formatDate(notif.created_at)}</div>
              </div>
              <div class="notification-actions">
                ${notif.notification_type === "share_revoked" ? `<button class="btn-recover" title="${escapeAttr(t("share.inbox.recoverTitle"))}">${escapeHtml(t("share.inbox.recover"))}</button>` : ""}
                ${notif.notification_type === "access_request" ? `<button class="btn-grant" title="${escapeAttr(t("share.inbox.grantTitle"))}">${escapeHtml(t("share.inbox.grant"))}</button>` : ""}
                <button class="btn-dismiss" title="${escapeAttr(t("share.inbox.dismiss"))}">✕</button>
              </div>
            </div>
          `,
            )
            .join("")}
        </div>
      `;
    }

    // Build invitations HTML
    let invitationsHtml = "";
    if (invitations.length > 0) {
      invitationsHtml = `
        <div class="invitations-section">
          <h4 class="section-title">${escapeHtml(t("share.inbox.invitations"))}</h4>
          ${invitations
            .map(
              (inv) => `
            <div class="invitation-item" data-id="${escapeAttr(inv.id)}">
              <div class="invitation-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="18" cy="5" r="3"/>
                  <circle cx="6" cy="12" r="3"/>
                  <circle cx="18" cy="19" r="3"/>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                </svg>
              </div>
              <div class="invitation-info">
                <div class="invitation-title">${escapeHtml(inv.share_title)}</div>
                <div class="invitation-details">
                  ${htmlWithBoldName("share.inbox.from", inv.inviter_username || t("share.member.unknown"))}
                  <span class="invitation-role">${escapeHtml(roleInText(inv.role))}</span>
                </div>
                <div class="invitation-date">${formatDate(inv.created_at)}</div>
              </div>
              <div class="invitation-actions">
                <button class="btn-accept" title="${escapeAttr(t("share.inbox.acceptTitle"))}">${escapeHtml(t("share.inbox.accept"))}</button>
                <button class="btn-decline" title="${escapeAttr(t("share.inbox.declineTitle"))}">${escapeHtml(t("share.inbox.decline"))}</button>
                <button class="btn-ignore" title="${escapeAttr(t("share.inbox.ignoreTitle"))}">${escapeHtml(t("share.inbox.ignore"))}</button>
              </div>
            </div>
          `,
            )
            .join("")}
        </div>
      `;
    }

    // Empty state if nothing
    let listHtml;
    if (invitations.length === 0 && notifications.length === 0) {
      listHtml = `
        <div class="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
            <polyline points="22,6 12,13 2,6"/>
          </svg>
          <p>${escapeHtml(t("share.inbox.empty"))}</p>
          <span>${escapeHtml(t("share.inbox.emptyHint"))}</span>
        </div>
      `;
    } else {
      listHtml = notificationsHtml + invitationsHtml;
    }

    const modal = createModal({
      id: modalId,
      title: t("share.inbox.title"),
      className: "modal-invitations",
      content: `
        <div class="invitations-list">
          ${listHtml}
        </div>
      `,
      buttons: [
        { text: t("common.close"), action: "close", className: "modal-btn-primary" },
      ],
    });

    let resolved = false;

    const cleanup = () => {
      closeModal(modalId);
      modal.remove();
    };

    const handleClose = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve(null);
    };

    // Event handlers
    modal
      .querySelector('[data-action="close"]')
      .addEventListener("click", handleClose);
    modal.querySelector(".modal-close")?.addEventListener("click", handleClose);
    modal
      .querySelector(".modal-backdrop")
      ?.addEventListener("click", handleClose);

    // Accept handlers
    modal.querySelectorAll(".btn-accept").forEach((btn) => {
      btn.addEventListener("click", async (e) => {
        const item = e.target.closest(".invitation-item");
        const invitationId = item.dataset.id;

        btn.disabled = true;
        btn.textContent = t("share.inbox.accepting");

        try {
          await acceptInvitation(invitationId);
          item.classList.add("accepted");
          item.querySelector(".invitation-actions").innerHTML =
            `<span class="status-accepted">${escapeHtml(t("share.inbox.accepted"))}</span>`;

          // Refresh modal after short delay
          setTimeout(() => {
            item.remove();
            if (modal.querySelectorAll(".invitation-item").length === 0 &&
                modal.querySelectorAll(".notification-item").length === 0) {
              handleClose();
            }
          }, 1000);
        } catch (error) {
          btn.disabled = false;
          btn.textContent = t("share.inbox.accept");
          if (error?.code === "cancelled") return;
          console.error("[Invitations] Error accepting:", error);
          alert(t("share.inbox.acceptError", { error: error?.message || t("share.inbox.tryAgain") }));
          // A withdrawn invitation was removed from the list by acceptInvitation.
          if (!invitations.some((i) => i.id === invitationId)) item.remove();
        }
      });
    });

    // Decline handlers
    modal.querySelectorAll(".btn-decline").forEach((btn) => {
      btn.addEventListener("click", async (e) => {
        const item = e.target.closest(".invitation-item");
        const invitationId = item.dataset.id;

        if (!confirm(t("share.inbox.declineConfirm"))) return;

        btn.disabled = true;
        btn.textContent = t("share.inbox.declining");

        try {
          await declineInvitation(invitationId);
          item.remove();

          if (modal.querySelectorAll(".invitation-item").length === 0 &&
              modal.querySelectorAll(".notification-item").length === 0) {
            handleClose();
          }
        } catch (error) {
          console.error("[Invitations] Error declining:", error);
          btn.disabled = false;
          btn.textContent = t("share.inbox.decline");
          alert(t("share.inbox.declineFailed"));
        }
      });
    });

    // "Always ignore": every invitation from this person, these and later ones
    // (Alex, 2026-10-26); undone from the account's settings.
    modal.querySelectorAll(".btn-ignore").forEach((btn) => {
      btn.addEventListener("click", async (e) => {
        const item = e.target.closest(".invitation-item");
        const invitationId = item.dataset.id;
        const name = invitations.find((i) => i.id === invitationId)?.inviter_username || t("share.member.unknown");
        if (!confirm(t("share.inbox.ignoreConfirm", { name }))) return;
        btn.disabled = true;
        try {
          const { dropped } = await ignoreInviter(invitationId);
          for (const id of dropped) {
            modal.querySelector(`.invitation-item[data-id="${CSS.escape(id)}"]`)?.remove();
          }
          showToast(t("share.inbox.ignored", { name }), { type: "success" });
          if (modal.querySelectorAll(".invitation-item").length === 0 &&
              modal.querySelectorAll(".notification-item").length === 0) {
            handleClose();
          }
        } catch (error) {
          console.error("[Invitations] Error ignoring:", error);
          btn.disabled = false;
          alert(t("share.inbox.ignoreFailed"));
        }
      });
    });

    // Recover handlers (share_revoked notifications)
    modal.querySelectorAll(".btn-recover").forEach((btn) => {
      btn.addEventListener("click", async (e) => {
        const item = e.target.closest(".notification-item");
        const notificationId = item.dataset.id;
        const shareId = item.dataset.shareId;
        const shareTitle = item.dataset.shareTitle;

        btn.disabled = true;
        btn.textContent = t("share.inbox.recovering");
        const dismissBtn = item.querySelector(".btn-dismiss");
        if (dismissBtn) dismissBtn.disabled = true;

        try {
          // Wait for the SHARE_REVOKED event to know the result
          const result = await new Promise((resolve) => {
            const timeout = setTimeout(() => resolve({ recoveryFailed: true }), 15000);
            const unsub = on(Events.SHARE_REVOKED, (data) => {
              if (data.shareId === shareId) {
                clearTimeout(timeout);
                unsub();
                resolve(data);
              }
            });
            emit(Events.REQUEST_SHARE_REVOCATION, {
              shareId,
              title: shareTitle,
              options: { fromNotification: true },
            });
          });

          // Mark notification as read (dismisses from other devices too)
          await markNotificationRead(notificationId).catch(() => {
            notifications = notifications.filter((n) => n.id !== notificationId);
            updateBadge();
          });

          if (result.recoveryFailed) {
            item.querySelector(".notification-actions").innerHTML =
              `<span class="status-declined">${escapeHtml(t("share.inbox.noData"))}</span>`;
          } else {
            item.querySelector(".notification-actions").innerHTML =
              `<span class="status-accepted">${escapeHtml(t("share.inbox.recovered"))}</span>`;
          }

          // Remove item after short delay
          setTimeout(() => {
            item.remove();
            if (modal.querySelectorAll(".invitation-item").length === 0 &&
                modal.querySelectorAll(".notification-item").length === 0) {
              handleClose();
            }
          }, 2000);
        } catch (error) {
          console.error("[Notifications] Error recovering:", error);
          btn.disabled = false;
          btn.textContent = t("share.inbox.recover");
          if (dismissBtn) dismissBtn.disabled = false;
        }
      });
    });

    // Grant-access handlers (access_request notifications).
    // Clicking "Grant access" dismisses the notification and opens the
    // doc's share modal with the requester's username pre-filled. The
    // owner picks a role + clicks Invite; invite_user on the server
    // detects the pending request and auto-accepts for the requester.
    modal.querySelectorAll(".btn-grant").forEach((btn) => {
      btn.addEventListener("click", async (e) => {
        const item = e.target.closest(".notification-item");
        const notificationId = item.dataset.id;
        const docId = item.dataset.docId;
        const requesterUsername = item.dataset.requesterUsername;
        if (!docId || !requesterUsername) return;
        btn.disabled = true;
        try {
          await markNotificationRead(notificationId).catch(() => {});
          item.remove();
          handleClose();
          emit(Events.OPEN_SHARE_MODAL_FOR_REQUEST, {
            docId,
            requesterUsername,
            docTitle: item.dataset.docTitle || "",
          });
        } catch (err) {
          console.error("[Notifications] Failed to grant access:", err);
          btn.disabled = false;
        }
      });
    });

    // Notification dismiss handlers
    modal.querySelectorAll(".btn-dismiss").forEach((btn) => {
      btn.addEventListener("click", async (e) => {
        const item = e.target.closest(".notification-item");
        const notificationId = item.dataset.id;
        const notifType = item.dataset.type;
        const dismissedShareId = item.dataset.shareId;

        // Warn before dismissing share_revoked without recovering
        if (notifType === "share_revoked") {
          if (!confirm(t("share.inbox.dismissConfirm"))) return;
        }

        btn.disabled = true;

        try {
          await markNotificationRead(notificationId);
          item.remove();

          // Dismissing a share_revoked notification without recovering is
          // an explicit "I don't want this anymore". Purge the local
          // IndexedDB cache so the stale snapshot can't be displayed as
          // live content the next time the user opens a doc that links
          // this one — and clear the now-useless vault entry.
          if (notifType === "share_revoked" && dismissedShareId) {
            try {
              const { purgeLinkedDoc } = await import("../../../engine/sync/crdt-bridge.js");
              await purgeLinkedDoc(dismissedShareId);
            } catch (err) {
              console.warn("[Notifications] purgeLinkedDoc failed on dismiss:", err);
            }
            try {
              await VaultStorage.deleteDoc?.(dismissedShareId);
            } catch (err) {
              console.warn("[Notifications] vault deleteDoc failed on dismiss:", err);
            }
          }

          // Check if both sections are now empty
          const hasInvitations =
            modal.querySelectorAll(".invitation-item").length > 0;
          const hasNotifications =
            modal.querySelectorAll(".notification-item").length > 0;

          // Remove empty notifications section header
          if (!hasNotifications) {
            const notifSection = modal.querySelector(".notifications-section");
            if (notifSection) {
              notifSection.remove();
            }
          }

          // Auto-close if nothing left
          if (!hasInvitations && !hasNotifications) {
            handleClose();
          }
        } catch (error) {
          console.error("[Invitations] Error dismissing notification:", error);
          btn.disabled = false;
          alert(t("share.inbox.dismissFailed"));
        }
      });
    });

    // Show modal
    document.body.appendChild(modal);
    showModal(modalId);
  });
}

// ============================================
// Utility Functions
// ============================================

// escapeHtml / escapeAttr come from engine/core/validation.js (imported at top).

function formatDate(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;

  // Less than a day
  if (diff < 86400000) {
    const hours = Math.floor(diff / 3600000);
    if (hours < 1) {
      const minutes = Math.floor(diff / 60000);
      return minutes <= 1 ? t("share.inbox.justNow") : formatRelative(-minutes, "minute");
    }
    return formatRelative(-hours, "hour");
  }

  // Less than a week
  if (diff < 604800000) {
    const days = Math.floor(diff / 86400000);
    return days === 1 ? t("common.yesterday") : formatRelative(-days, "day");
  }

  // Otherwise show date (numeric, in the app's language)
  return formatLocaleDate(date, {});
}

/**
 * A translated text whose {name} is shown in bold, e.g. "From <strong>ann</strong>".
 * The text and the name are escaped; only the <strong> is markup.
 */
function htmlWithBoldName(key, name) {
  // A function, so "$&"-like sequences in a name are not replacement patterns.
  return escapeHtml(t(key)).replace("{name}", () => `<strong>${escapeHtml(name)}</strong>`);
}

/**
 * The title and message of a server notification, written on this device in
 * the app's language from its type and data. The server stores English texts
 * (orbital_backend handlers/shares); the English texts here are the same.
 * Types without a case here keep the server's texts.
 * @param {{type: string, title?: string, message?: string, metadata?: Object}} notification
 * @param {{mapTitle?: string|null, mapTitles?: string[]}} [local] - titles from
 *   our own vault (map titles are end-to-end encrypted, the server has none)
 * @returns {{title: string, message: string}}
 */
function describeNotification(notification, { mapTitle = null, mapTitles = [] } = {}) {
  const data = notification.metadata || {};
  switch (notification.type) {
    case "share_revoked": {
      let title;
      if (mapTitle) title = t("share.notif.revoked.named", { title: mapTitle });
      else if (data.has_snapshot) title = t("share.notif.revoked.title");
      else if (data.share_title) title = t("share.notif.revoked.titled", { title: data.share_title });
      else title = t("share.notif.revoked.titleNoSnapshot");
      const message = data.has_snapshot
        ? t("share.notif.revoked.messageSnapshot")
        : t("share.notif.revoked.messageNoSnapshot");
      return { title, message };
    }
    case "access_request": {
      const name = data.requester_username;
      let title = notification.title;
      if (mapTitle) title = t("share.notif.accessRequest.named", { name: name || t("share.notif.someone"), title: mapTitle });
      else if (name) title = t("share.notif.accessRequest.title", { name });
      return { title, message: t("share.notif.accessRequest.message") };
    }
    case "member_key_changed": {
      // Two senders: the member re-registered their key (to owners), or an
      // owner restored a member's access under a new key (`restored_by`, to
      // the other members).
      const title = data.username ? t("share.notif.keyChanged.title", { name: data.username }) : notification.title;
      let message = data.restored_by
        ? t("share.notif.keyChanged.restored")
        : t("share.notif.keyChanged.reregistered");
      if (mapTitles.length) {
        const maps = `${mapTitles.slice(0, 5).join(", ")}${mapTitles.length > 5 ? ", …" : ""}`;
        message = `${message} ${t("share.notif.keyChanged.maps", { maps })}`;
      }
      return { title, message };
    }
    default:
      return { title: notification.title, message: notification.message };
  }
}

/**
 * Pre-cache shared data when an invitation is accepted.
 * Fetches current state via REST, decrypts it, and stores in IndexedDB,
 * ensuring it's available for recovery if access is later revoked.
 * This does not disrupt the user's current activity.
 *
 * @param {string} shareId - The doc/share UUID
 * @param {string} title - Share title for logging
 */
async function preCacheSharedData(shareId, title) {
  try {
    console.log("[Invitations] Pre-caching shared data:", title);

    // Fetch the current CRDT state from the server
    const response = await fetch(`/api/shares/${shareId}/state`, {
      credentials: "include",
    });

    if (!response.ok) {
      console.warn(
        "[Invitations] Failed to fetch share state:",
        response.status,
      );
      return;
    }

    const data = await response.json();

    // The endpoint returns BOTH baseline (`state`) and any uncompacted
    // `deltas`. A doc with only deltas (never compacted) has state=null
    // but still has content — we must apply the deltas in that case.
    const baselineB64 = data.state || null;
    const deltaList = Array.isArray(data.deltas) ? data.deltas : [];
    if (!baselineB64 && deltaList.length === 0) {
      console.log("[Invitations] No state to cache (share is empty)");
      return;
    }

    // Get DEK via unified method (doc entry was just stored by acceptInvitation)
    const dekBytes = await VaultStorage.getDocDEK(shareId);
    const dek = await crypto.subtle.importKey(
      "raw",
      dekBytes,
      { name: "AES-GCM", length: 256 },
      false,
      ["decrypt"],
    );
    const aad = new TextEncoder().encode(shareId);

    // Each stored blob is `IV(12) || AES-GCM(compress(yjsUpdate))`. The
    // WS sync path compresses BEFORE encrypt; this pre-cache path was
    // skipping the matching decompression and feeding compressed bytes
    // straight to Y.applyUpdate, which fails the moment it hits a byte
    // outside Yjs's struct-type table.
    async function decryptAndDecompress(encryptedB64) {
      const encrypted = Uint8Array.from(atob(encryptedB64), (c) => c.charCodeAt(0));
      const iv = encrypted.slice(0, 12);
      const ciphertext = encrypted.slice(12);
      let plaintext;
      try {
        plaintext = await crypto.subtle.decrypt(
          { name: "AES-GCM", iv, additionalData: aad },
          dek,
          ciphertext,
        );
      } catch {
        // Legacy data encrypted without AAD.
        plaintext = await crypto.subtle.decrypt(
          { name: "AES-GCM", iv },
          dek,
          ciphertext,
        );
      }
      return decompress(new Uint8Array(plaintext));
    }

    // Apply baseline + deltas to a temporary Yjs doc.
    const tempDoc = new Y.Doc();
    if (baselineB64) {
      const baseline = await decryptAndDecompress(baselineB64);
      Y.applyUpdate(tempDoc, baseline);
    }
    for (const deltaB64 of deltaList) {
      try {
        const delta = await decryptAndDecompress(deltaB64);
        Y.applyUpdate(tempDoc, delta);
      } catch (err) {
        // Skip an undecryptable delta but keep applying the rest. A
        // partial cache is still useful for revocation recovery; better
        // than throwing the whole pre-cache away.
        console.warn("[Invitations] Skipping undecryptable delta:", err);
      }
    }

    // Store in IndexedDB using the same key pattern as shared docs
    const persistence = new IndexeddbPersistence(`orbital-${shareId}`, tempDoc);

    // Wait for persistence to sync (this writes to IndexedDB)
    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("Timeout saving to IndexedDB"));
      }, 5000);

      persistence.once("synced", () => {
        clearTimeout(timeout);
        resolve();
      });
    });

    // Clean up
    persistence.destroy();
    tempDoc.destroy();

    console.log("[Invitations] Pre-cache complete:", title);
  } catch (error) {
    console.error("[Invitations] Pre-cache failed:", error);
    // Non-fatal - user can still open the share later
  }
}

// ============================================
// Event Listeners (module-level, to break circular dependencies)
// ============================================

// Listen for dismiss requests from crdt-bridge module
// This breaks the circular dependency: crdt-bridge.js no longer imports from notifications.js
// Guard against duplicate registration on re-import; store unsubscribers for cleanup
if (_moduleUnsubscribers.length === 0) {
  _moduleUnsubscribers.push(on(Events.DISMISS_SHARE_NOTIFICATION, async ({ shareId }) => {
    // Mark that this share was handled via WS path. If the share_revoked
    // notification arrives later (different WS channel, possible delay),
    // it will be auto-dismissed instead of queued.
    _alreadyRevokedShares.add(shareId);
    await dismissShareRevokedNotification(shareId);
  }));

  // Listen for share revocation completion - dismiss notification since recovery is done.
  // Only acts for WS-initiated revocations (wasActive docs). When the user clicks "Recover"
  // in the notification modal, the button handler already dismisses the notification.
  _moduleUnsubscribers.push(on(Events.SHARE_REVOKED, async ({ shareId, recoveredDocId, title, recoveryFailed, wasActive }) => {
  if (shareId && wasActive) {
    console.log("[Notifications] Share revoked (was active), dismissing notification:", shareId);

    // Find the notification for this share
    const notif = notifications.find(
      (n) => n.notification_type === "share_revoked" && n.metadata?.share_id === shareId
    );

    if (notif) {
      await markNotificationRead(notif.id).catch(() => {
        notifications = notifications.filter((n) => n.id !== notif.id);
        updateBadge();
      });
    }
  }
  }));
  // Listen for notification dismissed on another device (cross-device sync)
  _moduleUnsubscribers.push(on(Events.USER_NOTIFICATION_DISMISSED, (notificationId) => {
    // Filter self-echo: if we dismissed this locally, ignore the WS broadcast
    if (_locallyDismissed.has(notificationId)) {
      _locallyDismissed.delete(notificationId);
      return;
    }
    const existed = notifications.some((n) => n.id === notificationId);
    const invExisted = invitations.some((i) => i.notificationId === notificationId);
    if (existed || invExisted) {
      console.log("[Notifications] Dismissed on another device:", notificationId);
      notifications = notifications.filter((n) => n.id !== notificationId);
      invitations = invitations.filter((i) => i.notificationId !== notificationId);
      updateBadge();
    }
  }));
} // end if (_moduleUnsubscribers.length === 0)

/**
 * Handle real-time notification received via WebSocket
 * @param {Object} notification - { id, type, title, message, metadata, timestamp }
 */
function handleRealTimeNotification(notification) {
  console.log("[Notifications] Real-time notification received:", notification.title);

  // Handle specific notification types
  if (notification.type === "share_revoked") {
    const shareId = notification.metadata?.share_id;

    // If this share was already recovered or is being recovered (WS `removed` path
    // for active docs), auto-dismiss the notification instead of queuing it.
    // Two guards: _alreadyRevokedShares (set by DISMISS_SHARE_NOTIFICATION event)
    // and isRevocationProcessed (set early in performShareRevocation, catches the
    // race where the notification arrives before the dismiss event fires).
    if (shareId && (_alreadyRevokedShares.has(shareId) || isRevocationProcessed(shareId))) {
      // Check if share still exists in vault — if so, it was re-shared after a
      // previous revocation. Clear stale flags and let the notification through.
      const stillInVault = VaultStorage.getDoc?.(shareId);
      if (!stillInVault) {
        // Truly already handled
        console.log("[Notifications] share_revoked already handled/in-progress, auto-dismissing:", shareId);
        _alreadyRevokedShares.delete(shareId);
        if (notification.id) {
          markNotificationRead(notification.id).catch(() => {});
        }
        return;
      }
      // Share was re-shared then re-revoked — clear stale flags, proceed to show notification
      console.log("[Notifications] share_revoked for re-shared doc, clearing stale flags:", shareId);
      _alreadyRevokedShares.delete(shareId);
    }

    // Enrich notification with document title from vault (backend can't include it
    // because the title is encrypted). Store it in metadata for the Recover button handler.
    const docTitle = shareId ? VaultStorage.getDoc?.(shareId)?.title || null : null;
    const text = describeNotification(notification, { mapTitle: docTitle });
    if (docTitle) {
      if (!notification.metadata) notification.metadata = {};
      notification.metadata.share_title = docTitle;
    }

    // Add to notifications list — recovery is deferred until user clicks "Recover"
    const exists = notifications.some((n) => n.id === notification.id);
    if (!exists) {
      notifications.unshift({
        id: notification.id,
        notification_type: notification.type,
        title: text.title,
        message: text.message,
        metadata: notification.metadata,
        created_at: new Date(notification.timestamp).toISOString(),
      });
      updateBadge();
    }
    console.log("[Notifications] share_revoked queued (recovery deferred to user action):", shareId);
  } else if (notification.type === "invitation_accepted") {
    // Another device accepted this invitation — remove from local list
    const invitationId = notification.metadata?.invitation_id;
    if (invitationId) {
      console.log("[Notifications] Invitation accepted on another device:", invitationId);
      invitations = invitations.filter((i) => i.id !== invitationId);
      updateBadge();
      emit(Events.INVITATIONS_UPDATED, { count: invitations.length });
    }
    // Also mark the notification itself as read (cleanup)
    if (notification.id) {
      markNotificationRead(notification.id).catch(() => {});
    }
  } else if (notification.type === "share_accepted") {
    // Recipient accepted our invitation — emit event for live UI updates
    const docId = notification.metadata?.doc_id;
    const invitationId = notification.metadata?.invitation_id;
    const accepter = notification.metadata?.accepter;
    if (docId) {
      emit(Events.SHARE_INVITATION_RESPONDED, { docId, invitationId, accepter });
    }
    // Auto-dismiss (no need to show in notification list)
    if (notification.id) {
      markNotificationRead(notification.id).catch(() => {});
    }
  } else if (notification.type === "share_invitation") {
    // A re-invitation after a revocation keeps the share_revoked notice
    // (Recover) until the invitation is accepted (S-M8): declining must
    // still leave the recovery available. acceptInvitation dismisses it.

    // Add invitation directly to local list from WebSocket data
    // Note: Backend sends "inviter" field, we map to "inviter_username"
    // IMPORTANT: Store notificationId so we can mark notification as read when invitation is handled
    const invitationId = notification.metadata?.invitation_id;
    if (invitationId && !invitations.some((i) => i.id === invitationId)) {
      // S-M1: new invitations carry the title encrypted for us; old ones
      // carried it in plaintext (metadata or notification title).
      const encryptedTitle = notification.metadata?.encrypted_title || null;
      // Since S-M1 the server titles every invitation "Shared map" (English):
      // show ours. Older invitations carried the map title in plaintext.
      const plaintextTitle = !encryptedTitle && notification.title !== SERVER_INVITATION_TITLE ? notification.title : null;
      invitations.unshift({
        id: invitationId,
        notificationId: notification.id, // Track notification ID for marking as read
        share_id: notification.metadata?.share_id,
        share_title: notification.metadata?.share_title || plaintextTitle || t("share.invite.defaultTitle"),
        ...(encryptedTitle && { encryptedTitle }),
        inviter_username: notification.metadata?.inviter || notification.metadata?.inviter_username,
        role: notification.metadata?.role || "viewer",
        created_at: new Date(notification.timestamp).toISOString(),
      });
      updateBadge();

      emit(Events.INVITATIONS_UPDATED, {
        count: invitations.length,
        newCount: 1,
      });
      resolveInvitationTitles();

      // This invitation answers the user's own access request. It used to
      // be accepted silently; the user now confirms before joining (S-L5).
      if (notification.metadata?.auto_accept) {
        promptJoinGrantedRequest(invitationId, notification.metadata?.share_id || notification.metadata?.doc_id);
      }
    }
  } else if (notification.type === "access_request") {
    // Owner's inbox: show as a plain notification with the Grant-access
    // handler wired in the modal renderer. S-M1: the request carries no
    // title; ours comes from our own vault. Older requests carried the
    // title the requester saw, in plaintext; ours wins when we have it.
    const exists = notifications.some((n) => n.id === notification.id);
    if (!exists) {
      const docId = notification.metadata?.doc_id;
      const localTitle = docId ? VaultStorage.getDoc?.(docId)?.title : null;
      const text = describeNotification(notification, { mapTitle: localTitle });
      notifications.unshift({
        id: notification.id,
        notification_type: notification.type,
        title: text.title,
        message: text.message,
        metadata: { ...notification.metadata, ...(localTitle && { doc_title: localTitle }) },
        created_at: new Date(notification.timestamp).toISOString(),
      });
      updateBadge();
    }
  } else if (notification.type === "member_key_changed") {
    // A member re-registered their key (owners: restore their access) or an
    // owner restored a member's access with a new key (A2: other members
    // should verify it). Map titles are E2EE: resolve them from the vault.
    const exists = notifications.some((n) => n.id === notification.id);
    if (!exists) {
      const titles = (notification.metadata?.doc_ids || [])
        .map((id) => VaultStorage.getDoc?.(id)?.title).filter(Boolean);
      const text = describeNotification(notification, { mapTitles: titles });
      notifications.unshift({
        id: notification.id,
        notification_type: notification.type,
        title: text.title,
        message: text.message,
        metadata: notification.metadata,
        created_at: new Date(notification.timestamp).toISOString(),
      });
      updateBadge();
    }
  } else {
    // Generic notification - add to list
    const exists = notifications.some((n) => n.id === notification.id);
    if (!exists) {
      notifications.unshift({
        id: notification.id,
        notification_type: notification.type,
        title: notification.title,
        message: notification.message,
        metadata: notification.metadata,
        created_at: new Date(notification.timestamp).toISOString(),
      });
      updateBadge();
    }
  }

  // Cap notifications array to prevent unbounded memory growth
  while (notifications.length > 200) {
    notifications.pop();
  }
}

// ============================================
// Exports
// ============================================

// Removed: `export { invitations }` — exposed mutable internal array.
// Use getInvitations() instead which returns a safe copy.
